import { useCallback, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type {
  CustomerResolutionStatus,
  WooCustomer,
  WooStoreConfig,
} from "../types";
import { openFluxmedOpenIdLogin } from "../utils/openIdLogin";

function mapCustomerError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível verificar seu cadastro na loja.";
}

/**
 * Resolve customer WooCommerce por e-mail do paciente.
 * Sempre busca por e-mail — se o cadastro foi removido na loja, exige nova associação.
 */
export function useWooCommerceCustomer(
  store: WooStoreConfig | null,
  userEmail: string,
) {
  const [customer, setCustomer] = useState<WooCustomer | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [status, setStatus] = useState<CustomerResolutionStatus>("idle");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const resolveTask = useCallback(
    async (params: { store: WooStoreConfig; email: string }) => {
      const { store: activeStore, email } = params;
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error("E-mail do usuário não disponível para associar à loja.");
      }

      return woocommerceStoreService.findCustomerByEmail(
        activeStore,
        normalizedEmail,
      );
    },
    [],
  );

  const { run: runResolve, loading, error } = useAsyncTask({
    task: resolveTask,
    mapError: mapCustomerError,
  });

  const resolveCustomer = useCallback(async () => {
    if (!store || !userEmail.trim()) {
      setStatus("error");
      return null;
    }

    setStatus("loading");
    try {
      const result = await runResolve({ store, email: userEmail });
      if (result) {
        setCustomer(result);
        setCustomerId(result.id);
        setStatus("found");
        return result;
      }
      setCustomer(null);
      setCustomerId(null);
      setStatus("login_required");
      return null;
    } catch {
      setStatus("error");
      return null;
    }
  }, [store, userEmail, runResolve]);

  const handleOpenIdLogin = useCallback(async () => {
    if (!store) return null;
    setIsLoggingIn(true);
    try {
      await openFluxmedOpenIdLogin(store);
      return resolveCustomer();
    } finally {
      setIsLoggingIn(false);
    }
  }, [store, resolveCustomer]);

  const refreshCustomer = useCallback(async () => {
    return resolveCustomer();
  }, [resolveCustomer]);

  const resetCustomer = useCallback(() => {
    setCustomer(null);
    setCustomerId(null);
    setStatus("idle");
  }, []);

  return {
    customer,
    customerId,
    status,
    loading: loading || isLoggingIn,
    error: error ?? null,
    resolveCustomer,
    handleOpenIdLogin,
    refreshCustomer,
    resetCustomer,
  };
}
